/**
 * Per `operator-payouts` capability — operator withdrawal request
 * tracking. One row per request. State machine:
 *
 *   pending  ─approve→ approved  ─mark-paid→  paid
 *      │                  │
 *      └─reject→ rejected └─reject→ rejected  (releases hold)
 *
 * Storage: DDB `nx-operator-payouts-{env}`
 *   PK: operatorId
 *   SK: requestId (uuid)
 *   GSI: status-time-index (PK status, SK requestedAt) for admin queue
 *
 * Amount accounting:
 *   - On request submit: amount deducted from operator.pendingBalanceTwd
 *     via DDB conditional write `pendingBalanceTwd >= amount`
 *   - On reject (from pending OR approved): amount ADDed back to operator.pendingBalanceTwd
 *   - On approve / mark-paid: no operator-row change (hold persists, then settled)
 */
import { z } from 'zod';
export const OperatorPayoutStatusEnum = z.enum(['pending', 'approved', 'rejected', 'paid']);
export const OperatorPayoutSchema = z.object({
    operatorId: z.string().min(1).max(128),
    /** uuid */
    requestId: z.string().min(1).max(64),
    /** NTD amount (= integer points 1:1) — locked at submit time */
    amount: z.number().int().min(1000),
    status: OperatorPayoutStatusEnum,
    /** Operator-supplied bank info at submit time (snapshot — operator may change bank later) */
    bankAccount: z.object({
        bankCode: z.string().max(20),
        accountNumber: z.string().max(40),
        accountHolder: z.string().max(80),
    }).optional(),
    requestedAt: z.string().datetime(),
    approvedAt: z.string().datetime().optional(),
    rejectedAt: z.string().datetime().optional(),
    paidAt: z.string().datetime().optional(),
    /** superadmin sub who performed the most recent transition */
    reviewedBy: z.string().optional(),
    /** Required when status = rejected; reason shown to operator. */
    rejectReason: z.string().max(500).optional(),
    /** Required note the superadmin enters when approving (放行). Internal
     *  audit trail; the same text is also appended to `actions[]`. */
    approveNote: z.string().max(500).optional(),
    /** Required when status = paid; e.g. "ATM-20260525-001" / bank txn ID */
    bankTxnRef: z.string().max(120).optional(),
});
export const PayoutRequestBodySchema = z.object({
    amount: z.number().int().refine((n) => n >= 1000, '最低 1,000').refine((n) => n % 100 === 0, '必須為 100 的倍數'),
    bankAccount: z.object({
        bankCode: z.string().min(3).max(20),
        accountNumber: z.string().min(5).max(40),
        accountHolder: z.string().min(1).max(80),
    }).optional(),
});
